<template>
  <q-page class="q-pa-xl">
    <div class="row fit align-center items-center q-mb-md">
      <span
        id="dashboard_title"
        class="text-h4"
      >{{ i18n.title }}</span>
      <span class="text-h5 text-muted q-px-md">|</span>

      <!-- Dashboard Selector -->
      <q-select
        v-model="state_dashboards_selected"
        :options="dashboards"
        emit-value
        map-options
        option-value="link"
        option-label="label"
        class="dashboard-view-selector text-h4"
        style="width: fit-content"
        dense
        borderless
        flat 
        :hide-dropdown-icon="dashboards.length === 1"
      />
    </div>

    <!-- Staking Info -->
    <p class="text-caption text-center q-mb-md">
      <q-icon
        name="sym_o_info"
        size="xs"
        class="q-mr-xs"
      />
      {{ i18n.staking_banner_message }}
    </p>

    <!-- Filters -->
    <div class="row justify-start items-start content-start align-start q-pt-md">
      <!-- ExchangeAccount -->
      <q-select
        v-model="exchange_account_filter_selected"
        outlined
        dense
        options-dense
        hide-bottom-space
        :options="$page.props.exchange_account_filter_options"
        option-label="name"
        option-value="id"
        popup-content-class="text-capitalize q-py-sm"
        clearable
        color="primary" 
        style="max-width: fit-content; width: 100%" 
        :class="{
          'select-filter': true,
          'select-filter-active': exchange_account_filter_selected != null,
          'q-mr-sm': true,
          'q-mb-sm': true,
        }"
      >
        <template v-slot:selected>
          <div>
            <template v-if="exchange_account_filter_selected == null">
              {{ i18n.exchange_account_filter_all }}
            </template>
            <template v-else>
              {{ exchange_account_filter_selected.name }}
            </template>
          </div>
        </template>

        <template v-slot:prepend>
          <template v-if="exchange_account_filter_selected != null">
            <q-avatar
              size="18px"
              square
            >
              <img
                :src="exchange_account_filter_selected.logo_url"
                :alt="exchange_account_filter_selected.exchange_name"
              >
            </q-avatar>
          </template>
        </template>

        <template v-slot:option="scope">
          <q-item
            v-bind="scope.itemProps"
            style="min-width: 320px"
            :class="{ 'text-accent text-bold': scope.opt == exchange_account_filter_selected }"
          >
            <q-item-section
              avatar
              class="q-mr-none q-pr-none"
            >
              <q-avatar
                v-if="scope.opt"
                size="22px"
                square
              >
                <img
                  :src="scope.opt.logo_url"
                  :alt="scope.opt"
                >
              </q-avatar>
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ scope.opt.name }}</q-item-label>
            </q-item-section>
            <q-item-section
              side
              class="q-ml-lg"
            >
              <q-item-label class="text-bold">
                {{ scope.opt.balance }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>

        <!-- No accounts -->
        <template v-slot:no-option>
          <q-item dense>
            <q-item-section>
              <q-item-label class="">
                {{ i18n.exchange_account_filter_none }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>
      </q-select>

      <q-space />

      <!-- Date -->
      <DatePicker
        :min-date="balance_history_first"
        :max-date="balance_history_last"
        :selected-date="date_picker_formatted_date"
        :tooltip-next="i18n.day_next"
        :tooltip-previous="i18n.day_previous"
        @update:selected-date="on_date_picker_click($event)"
      />
    </div>

    <!-- Graph Balance History -->
    <div class="row full_width q-col-gutter-lg q-pt-md">
      <div class="col-12">
        <q-card class="fit">
          <q-card-section class="row items-center justify-space-between q-pt-sm q-pb-none">
            <div class="col-shrink align-center justify-center item-center q-card-title q-py-sm gt-xs row items-center no-wrap">
              <span>{{ $page.props.balances.total_label }}</span>
              <span class="text-muted text-caption q-ml-sm">
                &middot; {{ $page.props.balances.day }} ({{ $page.props.balances.at }} {{ $page.props.balances.time }})
              </span>
            </div>
            <div class="col text-right">
              <q-btn-toggle
                v-model="balance_timerange"
                unelevated
                class="date-range-btn"
                size="sm"
                toggle-color="primary"
                :options="balance_timeranges"
              />
            </div>
          </q-card-section>
          <q-card-section class="q-pt-none">
            <!-- Balance -->
            <q-item class="q-pa-none">
              <q-item-section>
                <q-item-label class="">
                  <span
                    class="text-bold"
                    style="font-size: min(4vw, 28px); font-family: 'Inconsolata', monospace; font-weight: 600"
                  >
                    {{ $page.props.balances.total_amount }}</span>
                </q-item-label>
              </q-item-section>
            </q-item>
            <!-- /Balance -->
          </q-card-section>
          <q-card-section
            v-if="balance_history.length"
            class="q-pb-md q-pt-none"
          >
            <Line
              ref="history_graph_ref"
              :data="chart_data"
              :options="chart_options"
              :height="270"
            />
          </q-card-section>
          <q-card-section
            v-else
            class="self-stretch q-pt-md"
            style="height: 270px"
          >
            <div class="text-center row items-center justify-center q-pa-none fit full-height text-light self-stretch">
              <div class="col-12">
                <!-- No Data / Ghost -->
                <iframe
                  type="image/svg+xml"
                  src="/animations/ghost_animation.svg"
                  class="inline"
                  style="height: 140px"
                  sandbox="allow-scripts"
                />
                <p class="text-default">
                  {{ i18n.no_history_available }}
                </p>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <div class="row full_width q-col-gutter-lg q-mt-sm">
      <!-- Assets -->
      <div class="col-xs-12 col-sm-12 col-md-6 self-stretch">
        <q-card class="fit">
          <q-card-section class="row items-center justify-space-between q-pb-none">
            <div class="col">
              <span class="q-card-title">{{ i18n.table_title }}</span>
            </div>
            <div class="col-auto">
              <a
                :href="assets_table_export_path"
                target="_blank"
                download
              >
                <q-btn
                  flat
                  round
                  dense
                  icon="fas fa-download"
                  color="primary"
                  size="sm"
                >
                  <q-tooltip>{{ i18n.export_csv }}</q-tooltip>
                </q-btn>
              </a>
            </div>
          </q-card-section>

          <q-card-section class="row items-start justify-space-between q-pt-md q-px-none">
            <q-table
              id="assets-table"
              v-model:pagination="assets_table_pagination"
              :rows="assets_table"
              :columns="assets_table_columns"
              row-key="id"
              class="full-width"
              flat
              dense
              :no-data-label="$q.lang.table.noResults"
              separator="none"
              hide-pagination 
              style="max-height: 340px" 
              :rows-per-page-options="[0]"
            >
              <template v-slot:body-cell-asset_symbol="props">
                <q-td
                  :props="props"
                  class=" "
                >
                  <div class="col-shrink">
                    <!-- Asset Icon -->
                    <img
                      width="18px"
                      height="18px"
                      class="inline q-mr-xs"
                      :alt="props.value"
                      :src="props.row.asset_icon"
                    >

                    {{ props.row.asset_symbol_truncated }}
                    <q-tooltip
                      v-if="props.value != props.row.asset_symbol_truncated"
                      :delay="500"
                      anchor="bottom middle"
                      self="top middle"
                    >
                      {{ props.value }}
                    </q-tooltip>
                  </div>
                </q-td>
              </template>

              <template v-slot:body-cell-fiat_value_formatted="props">
                <q-td
                  :props="props"
                  class=" "
                >
                  <span class="text-bold">
                    {{ props.value }}
                    <q-tooltip
                      :delay="500"
                      anchor="center start"
                      self="center right"
                      :offset="[50, 0]"
                    >{{ props.row.change_rate_text }}</q-tooltip>
                  </span>
                  <q-badge
                    v-if="props.row.change_rate == null || props.row.change_rate == '0.0'"
                    color="transparent"
                  >
                    <q-icon
                      name="fas la-exclamation-triangle"
                      color="warning"
                      size="14px"
                    />
                    <q-tooltip
                      anchor="center left"
                      self="center right"
                      class="bg-warning text-white"
                    >
                      <span>{{ i18n.table_column_value_error }}</span>
                    </q-tooltip>
                  </q-badge>
                </q-td>
              </template>
            </q-table>
          </q-card-section>
        </q-card>
      </div>

      <!-- Pie Graph by Assets -->
      <div class="col-xs-12 col-sm-12 col-md-6">
        <q-card class="fit">
          <q-card-section class="row items-center justify-space-between q-pb-none">
            <div class="col">
              <span class="q-card-title">{{ i18n.pie_title }}</span>
            </div>
          </q-card-section>

          <q-card-section class="row items-center justify-space-around text-center">
            <q-card-section
              class="self-stretch q-pt-md q-mx-auto"
              style="height: 320px"
            >
              <Doughnut
                v-if="$page.props.assets_pie.datasets[0].data.length > 0"
                ref="assets_repartition_donut_ref"
                :data="$page.props.assets_pie"
                :options="assets_pie_options"
              />
              <div
                v-else
                class="text-center row items-center justify-center q-pa-md fit full-height text-default"
              >
                <div class="col-12">
                  <img
                    class="inline"
                    src="/illustrations/chart_pie_icon.png"
                    width="78"
                  >

                  <p class="text-muted q-pt-md">
                    {{ i18n.pie_not_available }}
                  </p>
                </div>
              </div>
            </q-card-section>
          </q-card-section>
        </q-card>
      </div>
    </div>

  </q-page>
</template>
<script>
import DatePicker from '@/pages/w/dashboard/date_picker.vue';
import Layout from '@/layouts/workspace.vue';

import { CategoryScale } from "chart.js";
import { Chart as ChartJS } from "chart.js";
import { Filler } from "chart.js";
import { Interaction } from "chart.js";
import { Line } from "vue-chartjs";
import { LineElement } from "chart.js";
import { LinearScale } from "chart.js";
import { PointElement } from "chart.js";
import { TimeScale } from "chart.js";
import { Tooltip } from "chart.js";
import { CrosshairPlugin } from "chartjs-plugin-crosshair";
import { Interpolate } from "chartjs-plugin-crosshair";
import annotationPlugin from "chartjs-plugin-annotation";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-date-fns";
import { date as qdate } from "quasar";

const CustomCrosshairPlugin = function (plugin) {
  const originalAfterDraw = plugin.afterDraw;
  plugin.afterDraw = function (chart, easing) {
    if (chart && chart.crosshair) {
      originalAfterDraw.call(this, chart, easing);
    }
  };
  return plugin;
};

ChartJS.register(
  CategoryScale,
  LineElement,
  Tooltip,
  LinearScale,
  PointElement,
  TimeScale,
  Filler,
  annotationPlugin,
  zoomPlugin,
  CustomCrosshairPlugin(CrosshairPlugin)
);

Interaction.modes.interpolate = Interpolate;

import { Doughnut } from "vue-chartjs";
import { ArcElement } from "chart.js";
import { Legend } from "chart.js";
import { getChartLabelPlugin } from "chart.js-plugin-labels-dv";
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  getChartLabelPlugin()
);

export default {
  components: {
    DatePicker,
    Doughnut,
    Line,
  },
  layout: Layout,
  props: {
    i18n: {
      type: Object,
      require: true,
    },
    dashboards: {
      type: Array,
      required: true,
    },
    dashboards_selected: {
      type: Object,
      require: true,
    },
    exchange_account: {
      type: Object,
      require: false,
    },
    day: {
      type: String,
      require: true,
    },
    fiat: {
      type: String,
      require: true,
    },
    balance_history: {
      type: Array,
      required: true,
    },
    last_transaction_date: {
      type: String,
      required: false,
    },
    assets_table_export_path: {
      type: String,
      require: true,
    },
    assets_table: {
      type: Array,
      required: true,
    },
  },
  data() {

    let chart_width;
    let chart_height;
    let chart_fill_gradient;

    let balance_history_first = this.balance_history && this.balance_history.length > 0
      ? this.balance_history[0][0]
      : this.day;
    let balance_history_last = this.balance_history && this.balance_history.length > 0
      ? this.balance_history[this.balance_history.length - 1][0]
      : this.day;

    return {
      state_dashboards_selected: this.dashboards_selected,
      balance_timerange: "all",
      exchange_account_filter_selected: this.exchange_account,
      date_selected: this.day,
      balance_history_first: balance_history_first,
      balance_history_last: balance_history_last,
      chart_data: {
        // labels: this.balance_history.map((h) => h[0]),
        datasets: [
          {
            label: 'Balance',
            data: this.balance_history,
            borderColor: "rgb(111, 142, 236)",
            borderWidth: 2,
            backgroundColor: function (context) {
              const chart = context.chart;
              const { ctx, chartArea } = chart;
              if (!chartArea) {
                // This case happens on initial chart load
                return;
              }
              const current_width = chartArea.right - chartArea.left;
              const current_height = chartArea.bottom - chartArea.top;
              if (!chart_fill_gradient || chart_width !== current_width || chart_height !== current_height) {
                // Create the gradient because this is either the first render
                // or the size of the chart has changed
                chart_width = current_width;
                chart_height = current_height;
                chart_fill_gradient = ctx.createLinearGradient(0, 0, 0, current_height);
                chart_fill_gradient.addColorStop(0, "rgba(111, 142, 236, 0.2)"); // Start color
                chart_fill_gradient.addColorStop(1, "rgba(111, 142, 236, 0)"); // End color
              }
              return chart_fill_gradient;
            },
            fill: true,
            pointRadius: 0,
            pointHoverRadius: 6,
            tension: 0.4
          },
        ],
      },
      chart_options: function (
        i18n,
        history,
        date_annotation,
        last_transaction_annotation,
        on_click,
        currency_format,
        fiat,
      ) {
        let minDate = Infinity;
        let maxDate = Infinity;
        if (history.length) {
          minDate = new Date(history[0][0]).getTime();
          maxDate = new Date(history[history.length - 1][0]).getTime();
        }

        const yScale = {
          beginAtZero: true,
        };
        if (history.length) {
          const values = history
            .map((point) => parseFloat(point[1]))
            .filter((value) => Number.isFinite(value));
          if (values.length) {
            const yMax = Math.max(...values);
            const yPadding = Math.max(Math.abs(yMax) * 0.05, 1);
            yScale.suggestedMax = yMax + yPadding;
          }
        }

        const annotations = {
          currentDateAnnotation: date_annotation,
        };
        if (last_transaction_annotation) {
          annotations.lastTransactionAnnotation = last_transaction_annotation;
        }

        return {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false,
          },
          scales: {
            y: yScale,
            x: {
              min: minDate,
              max: maxDate,
              type: "time",
              time: {
                unit: "day",
                parser: "yyyy-MM-dd",
                displayFormats: {
                  day: "dd/MM/yy", // Adjusted format
                },
              },
              ticks: {
                autoSkip: true,
                maxTicksLimit: 10, // Adjust as needed to manage label density
              },
              grid: {
                display: false, // This hides the vertical grid lines
              },
            },
          },
          plugins: {
            legend: {
              display: false, // This hides the legend
            },
            annotation: {
              annotations,
            },
            tooltip: {
              enabled: true,
              mode: "index",
              backgroundColor: "#ffffff", // White background
              titleColor: "#000000", // Black title
              bodyColor: "#000000", // Black body
              padding: 12,
              caretSize: 0,
              caretPadding: 64,
              borderColor: "#eee",
              borderWidth: 1,
              cornerRadius: 8,
              displayColors: true,
              intersect: false,
              usePointStyle: true,
              callbacks: {
                labelPointStyle: function () {
                  return {
                    pointStyle: 'circle',
                    rotation: 0,
                  };
                },
                title: function (tooltipItems) {
                  return qdate.formatDate(tooltipItems[0].parsed.x, "DD/MM/YYYY");
                },
                label: function (tooltipItem) {
                  return `${i18n.balance}: ${currency_format(parseFloat(tooltipItem.raw[1]), fiat)}`;
                },
              }
            },
            zoom: {
              zoom: {
                wheel: {
                  enabled: true,
                },
                pinch: {
                  enabled: false,
                },
                mode: "x",
                scaleMode: "x",
              },
              pan: {
                enabled: true,
                mode: "x",
              },
              limits: {
                x: {
                  // min range is a 5 days in timestamp.
                  minRange: 5 * 24 * 60 * 60 * 1000,
                  min: minDate,
                  max: maxDate,
                },
              },
            },
            crosshair: {
              line: {
                color: "#ddd", // crosshair line color
                width: 1, // crosshair line width
              },
              sync: {
                enabled: false, // enable trace line syncing with other charts
              },
              zoom: {
                enabled: false, // enable zooming
              },
            },
          },
          onClick: on_click,
          interaction: {
            mode: "nearest",
            intersect: false,
            axis: "x",
          },
        };
      }(
        this.i18n,
        this.balance_history,
        this.date_annotation,
        this.last_transaction_date ? this.last_transaction_annotation : null,
        this.on_chart_click,
        this.currency_format,
        this.fiat,
      ),
      assets_table_pagination: {
        rowsPerPage: -1,
        sortBy: "fiat_value_formatted",
        descending: true,
      },
      assets_table_columns: [
        {
          name: "asset_symbol",
          align: "left",
          label: this.$page.props.i18n.table_column_currency,
          field: (row) => row.asset_symbol,
          sortable: true,
        },
        {
          name: "balance",
          align: "right",
          label: this.$page.props.i18n.table_column_balance,
          field: (row) => row.balance,
          sortable: true,
        },
        {
          name: "fiat_value_formatted",
          align: "right",
          field: (row) => row.fiat_value_formatted,
          label: this.$page.props.i18n.table_column_value,
          sort: (a, b, row_a, row_b) => parseFloat(row_a.fiat_value) - parseFloat(row_b.fiat_value),
          sortable: true,
        },
      ],
      assets_pie_options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "40%",
        plugins: {
          crosshair: false,
          tooltip: {
            enabled: true,
            backgroundColor: "#ffffff", // White background
            titleColor: "#000000", // Black title
            bodyColor: "#000000", // Black body
            padding: 12,
            callbacks: {
              title: function (context) {
                if (context.length > 0) {
                  const item = context[0];
                  return item.dataset.titles[item.dataIndex];
                }
                return "";
              },
              label: function (item) {
                return item.dataset.labels[item.dataIndex];
              },
            },
          },
          legend: {
            display: false,
          },
          labels: [
            {
              render: function (args) {
                return args.label;
              },
              overlap: false,
              fontStyle: 'bold',
            }
          ],
        },
      },
    };
  },
  computed: {
    date_picker_formatted_date() {
      return qdate.formatDate(this.date_selected, "YYYY/MM/DD");
    },
    balance_timeranges() {
      return [
        { label: this.i18n.time_range_1_week, value: '1w' },
        { label: this.i18n.time_range_1_month, value: '1m' },
        { label: this.i18n.time_range_3_months, value: '3m' },
        { label: this.i18n.time_range_ytd, value: 'ytd' },
        { label: this.i18n.time_range_all, value: 'all' }
      ];
    },
  },
  watch: {
    exchange_account_filter_selected(new_value) {
      this.$inertia.get(
        '#',
        {
          day: this.date_selected,
          exchange_account: new_value?.id,
        },
        {
          preserveScroll: true,
        }
      );
    },
    state_dashboards_selected(new_value) {
      this.$inertia.get(new_value);
    },
    balance_timerange(new_value) {
      if (!this.balance_history.length) {
        return;
      }

      const graph_min = new Date(this.balance_history_first);
      const graph_max = new Date(this.balance_history_last);
      const target = this.date_selected != null ? new Date(this.date_selected) : new Date(graph_max);
      let window_min = new Date(graph_min);
      let window_max = new Date(graph_max);

      switch (new_value) {
      case '1w':
        // Sliding Week

        window_min = new Date(new Date(target).setDate(target.getDate() - 3));
        window_max = new Date(new Date(target).setDate(target.getDate() + 3));
        if (window_min < graph_min) {
          window_min = new Date(graph_min);
          window_max = new Date(new Date(graph_min).setDate(graph_min.getDate() + 7));
        }
        if (window_max > graph_max) {
          window_min = new Date(new Date(graph_max).setDate(graph_max.getDate() - 7));
          window_max = new Date(graph_max);
        }
        break;
      case '1m':
        // Discrete Month

        window_min = new Date(new Date(target).setDate(1));
        // Last day of the target month
        window_max = new Date(new Date(target).setDate(1));
        window_max.setMonth(window_max.getMonth() + 1);
        window_max.setDate(0);
        if (window_min < graph_min) {
          window_min = new Date(graph_min);
          // Last day of the month
          window_max = new Date(graph_min);
          window_max.setMonth(window_max.getMonth() + 1);
          window_max.setDate(0);
        }
        if (window_max > graph_max) {
          window_min = new Date(graph_max);
          window_min.setDate(1);
          window_max = new Date(graph_max);
        }
        break;
      case '3m':
        // Discrete 3 Months

        window_min = new Date(new Date(target).setDate(1));
        window_min.setMonth(window_min.getMonth() - 1);
        // Last day of the last month
        window_max = new Date(new Date(target).setDate(1));
        window_max.setMonth(window_max.getMonth() + 2);
        window_max.setDate(0);

        if (window_min < graph_min) {
          window_min = new Date(graph_min);
          // Last day of the month
          window_max = new Date(graph_min);
          window_max.setMonth(window_max.getMonth() + 3);
          window_max.setDate(0);
        }
        if (window_max > graph_max) {
          window_min = new Date(graph_max);
          window_min.setMonth(window_min.getMonth() - 3);
          window_min.setDate(1);
          window_max = new Date(graph_max);
        }
        break;
      case 'ytd':
        // Sliding Year
        window_min = new Date(new Date(target));
        window_min.setYear(target.getFullYear() - 1);
        window_max = new Date(new Date(target));
        break;
      case 'all':
        break;
      }

      if (window_min < graph_min) {
        window_min = graph_min;
      }
      if (window_max > graph_max) {
        window_max = graph_max;
      }

      // Update the X-axis to display the range
      let line = this.$refs.history_graph_ref;
      if (line && line.chart) {
        const chart = line.chart;
        chart.options.scales.x.min = window_min.getTime();
        chart.options.scales.x.max = window_max.getTime();
        chart.update();
      }
    }
  },
  methods: {
    change_day(day) {
      this.$inertia.get(
        '#',
        {
          day: day,
          exchange_account: this.exchange_account_filter_selected?.id,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'exchange_account',
            'day',
            'balance_history',
            'balances',
            'assets_table',
            'assets_table_export_path',
            'assets_pie',
          ],
        },
      );
    },
    chart_update() {
      let line = this.$refs.history_graph_ref;
      const chart = line.chart;
      chart.update();
    },
    on_chart_click(event, activeElements) {
      if (activeElements.length > 0) {
        this.date_selected = activeElements[0].element.$context.raw[0]
        this.chart_update();
        this.change_day(this.date_selected);
      }
    },
    on_date_picker_click(event) {
      this.date_selected = event.replaceAll("/", "-");
      this.change_day(this.date_selected);
      this.chart_update();
    },
    date_annotation() {
      return {
        type: "line",
        mode: "vertical",
        scaleID: "x",
        value: Date.parse(this.date_selected),
        borderColor: "#bbb",
        borderWidth: 2,
        borderDash: [8, 12],
        label: {
          display: false,
        },
      };
    },
    last_transaction_annotation() {
      const parsedDate = Date.parse(this.last_transaction_date);
      const formattedDate = qdate.formatDate(this.last_transaction_date, this.i18n.date_format);

      return {
        type: "line",
        mode: "vertical",
        scaleID: "x",
        value: parsedDate,
        borderColor: "#ff9800",
        borderWidth: 2,
        borderDash: [4, 6],
        clip: false,
        label: {
          display: true,
          content: `${this.i18n.last_transaction_marker} (${formattedDate})`,
          position: "start",
          backgroundColor: "rgb(255,242,229)",
          color: "#ff9800",
          font: {
            size: 11,
          },
          padding: 6,
          yAdjust: -8,
        },
      };
    },
    currency_format(value, symbol) {
      return new Intl.NumberFormat(
        "fr-FR",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          style: "currency",
          currency: symbol,
        }
      ).format(value);
    },
  },
};
</script>

<style>
.dashboard-view-selector .q-field__native span {
  color: var(--text-muted) !important;
}
</style>
